import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
// TODO: Re-enable when premium gating is restored
// import { useAuthStore } from '@/store/auth'
import { getActivity, type Activity } from '@/services/content'
import { activityApi, reminderApi, type RepeatConfig, type Reminder } from '@/services/api'
import Button from '@/components/ui/Button'
import ActivityTimer from '@/components/ui/ActivityTimer'
import ReminderPicker from '@/components/ui/ReminderPicker'
import CustomReminderModal from '@/components/ui/CustomReminderModal'
import ReminderModal from '@/components/ui/ReminderModal'
import ActivityImageCarousel from '@/components/ui/ActivityImageCarousel'
import { useEnsureAccess, LockedContentView } from '@/components/Gate'
import { gateState } from '@/store/access'
import styles from './ActivityDetail.module.css'
import Icon from '@/components/ui/Icon'

// Check if user has seen bell tooltip before
const BELL_TOOLTIP_KEY = 'mellomap_bell_tooltip_seen'

export default function ActivityDetail() {
  const { activityId } = useParams<{ activityId: string }>()
  const navigate = useNavigate()
  // TODO: Re-enable when premium gating is restored
  // const user = useAuthStore((state) => state.user)
  const [activity, setActivity] = useState<Activity | null>(null)
  const [loading, setLoading] = useState(true)
  const [started, setStarted] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [isSaved, setIsSaved] = useState(false)
  const [savedId, setSavedId] = useState<number | null>(null)
  const [logId, setLogId] = useState<number | null>(null)
  const [showReminderPicker, setShowReminderPicker] = useState(false)
  const [showCustomReminder, setShowCustomReminder] = useState(false)
  const [reminderSaved, setReminderSaved] = useState(false)
  const [showSaveToast, setShowSaveToast] = useState(false)
  // New state for reminder modal and badge
  const [reminders, setReminders] = useState<Reminder[]>([])
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showBellTooltip, setShowBellTooltip] = useState(false)
  const [lastSavedReminder, setLastSavedReminder] = useState<Reminder | null>(null)
  const [editingReminder, setEditingReminder] = useState<Reminder | null>(null)
  const [activePanel, setActivePanel] = useState(0)
  const [whyExpanded, setWhyExpanded] = useState(false)
  const [whenExpanded, setWhenExpanded] = useState(false)
  const { access } = useEnsureAccess()

  useEffect(() => {
    if (!activityId) return

    // Load activity, saved status, and reminders
    Promise.all([
      getActivity(activityId),
      activityApi.getSaved().catch(() => ({ data: [] })),
      reminderApi.getReminders({ activity_id: activityId }).catch(() => ({ data: [] })),
    ])
      .then(([act, savedRes, remindersRes]) => {
        setActivity(act || null)
        const saved = (savedRes.data || []).find(
          (s: { activity_id: string; id: number }) => s.activity_id === activityId
        )
        if (saved) {
          setIsSaved(true)
          setSavedId(saved.id)
        }
        // Filter for pending reminders only
        const pendingReminders = (remindersRes.data || []).filter(
          (r: Reminder) => r.status === 'pending'
        )
        setReminders(pendingReminders)
      })
      .finally(() => setLoading(false))

    // Check if should show bell tooltip (first-time user)
    const hasSeenTooltip = localStorage.getItem(BELL_TOOLTIP_KEY)
    if (!hasSeenTooltip) {
      // Show tooltip after a short delay
      const timer = setTimeout(() => setShowBellTooltip(true), 1000)
      return () => clearTimeout(timer)
    }
  }, [activityId])

  const handleSave = async () => {
    if (!activityId) return

    try {
      if (isSaved && savedId) {
        await activityApi.unsaveActivity(String(savedId))
        setIsSaved(false)
        setSavedId(null)
        setShowSaveToast(false)
      } else {
        const res = await activityApi.saveActivity(activityId)
        setIsSaved(true)
        setSavedId(res.data.id)
        // Show save toast with reminder prompt
        setShowSaveToast(true)
        // Auto-hide after 5 seconds
        setTimeout(() => setShowSaveToast(false), 5000)
      }
    } catch {
      // Handle error
    }
  }

  const handleReminderSave = async (scheduledAt: Date, repeatConfig?: RepeatConfig) => {
    if (!activityId || !activity) return

    try {
      const response = await reminderApi.createReminder({
        activity_id: activityId,
        activity_title: activity.title,
        scheduled_at: scheduledAt.toISOString(),
        repeat_config: repeatConfig || { type: 'once' },
      })
      // Store the saved reminder for enhanced toast
      setLastSavedReminder(response.data)
      setReminderSaved(true)
      setShowReminderPicker(false)
      setShowCustomReminder(false)
      setShowReminderModal(false)
      // Refresh reminders list
      const remindersRes = await reminderApi.getReminders({ activity_id: activityId })
      const pendingReminders = (remindersRes.data || []).filter(
        (r: Reminder) => r.status === 'pending'
      )
      setReminders(pendingReminders)
      // Show success toast for 5 seconds
      setTimeout(() => {
        setReminderSaved(false)
        setLastSavedReminder(null)
      }, 5000)
    } catch {
      alert('Failed to save reminder. Please try again.')
    }
  }

  const handleReminderDelete = async (reminderId: string) => {
    try {
      await reminderApi.deleteReminder(reminderId)
      setReminders(reminders.filter(r => r.id !== reminderId))
    } catch {
      alert('Failed to delete reminder. Please try again.')
    }
  }

  const handleReminderUpdate = async (reminderId: string, scheduledAt: Date, repeatConfig?: RepeatConfig) => {
    try {
      const response = await reminderApi.updateReminder(reminderId, {
        scheduled_at: scheduledAt.toISOString(),
        repeat_config: repeatConfig,
      })
      setReminders(reminders.map(r => r.id === reminderId ? response.data : r))
      setEditingReminder(null)
      setShowCustomReminder(false)
      setLastSavedReminder(response.data)
      setReminderSaved(true)
      setTimeout(() => {
        setReminderSaved(false)
        setLastSavedReminder(null)
      }, 5000)
    } catch {
      alert('Failed to update reminder. Please try again.')
    }
  }

  const dismissBellTooltip = () => {
    setShowBellTooltip(false)
    localStorage.setItem(BELL_TOOLTIP_KEY, 'true')
  }

  const handleBellClick = () => {
    dismissBellTooltip()
    setShowReminderModal(true)
  }

  const handleStart = async () => {
    if (!activityId) return

    try {
      // Create activity log when starting
      const res = await activityApi.createLog({ activity_id: activityId })
      setLogId(res.data.id)
    } catch {
      // Continue even if logging fails
    }
    setStarted(true)
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }

  if (!activity) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Activity not found</div>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    )
  }

  // Content gate: trial users spend an unlock credit; expired/no-credit
  // users are sent to the paywall. Premium/unlocked pass through.
  const gate = gateState(access, 'activity', activity.id)
  if (gate === 'loading') {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading...</div>
      </div>
    )
  }
  if (gate !== 'premium' && gate !== 'unlocked') {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
        </header>
        <LockedContentView
          type="activity"
          id={activity.id}
          title={activity.title}
          onUnlocked={() => {}}
        />
      </div>
    )
  }

  // Activity in progress view
  if (started) {
    const step = activity.steps[currentStep]
    const isLastStep = currentStep === activity.steps.length - 1

    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <button className={styles.backButton} onClick={() => setStarted(false)}>
            ← Exit
          </button>
          <span className={styles.stepCounter}>
            Step {currentStep + 1} of {activity.steps.length}
          </span>
        </header>

        <div className={styles.progress}>
          <div
            className={styles.progressBar}
            style={{ width: `${((currentStep + 1) / activity.steps.length) * 100}%` }}
          />
        </div>

        <ActivityTimer durationMinutes={activity.duration} />

        <section className={styles.stepContent}>
          {activity.images && activity.images[currentStep] && (
            <div className={styles.stepImage}>
              <img src={activity.images[currentStep]} alt={`Step ${currentStep + 1}`} />
            </div>
          )}
          <p className={styles.stepInstruction}>{step.instruction}</p>
        </section>

        <footer className={styles.stepFooter}>
          {currentStep > 0 && (
            <Button
              variant="outline"
              onClick={() => setCurrentStep(currentStep - 1)}
            >
              Previous
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => {
              if (isLastStep) {
                // Activity complete - pass logId and activity title in state
                navigate(`/activity/${activityId}/complete`, {
                  state: { logId, activityTitle: activity.title }
                })
              } else {
                setCurrentStep(currentStep + 1)
              }
            }}
          >
            {isLastStep ? 'Complete' : 'Next'}
          </Button>
        </footer>
      </div>
    )
  }

  // Activity detail view (not started)
  const images = activity.images || []
  const hasImages = images.length > 0

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <div className={styles.headerActions}>
          <div className={styles.bellWrapper}>
            <button
              className={styles.reminderButton}
              onClick={handleBellClick}
              title="Set reminder"
            >
              
              {reminders.length > 0 && (
                <span className={styles.bellBadge}>{reminders.length}</span>
              )}
            </button>
            {showBellTooltip && (
              <div className={styles.bellTooltip}>
                <span>Tap to schedule this activity!</span>
                <button
                  className={styles.tooltipClose}
                  onClick={dismissBellTooltip}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}
          </div>
          <button className={styles.saveButton} onClick={handleSave}>
            {isSaved ? '' : ''}
            <span className={isSaved ? styles.savedText : ''}>
              {isSaved ? 'Saved' : 'Save'}
            </span>
          </button>
        </div>
      </header>

      {hasImages ? (
        <ActivityImageCarousel
          images={images}
          activeIndex={activePanel}
          onIndexChange={setActivePanel}
        />
      ) : (
        <div className={styles.heroBanner} data-situation={activity.situationIds[0]}>
          <span className={styles.heroBannerIcon}>
            <Icon
              name={{
                wont_sit_still: 'footprints',
                seeks_crashing: 'zap',
                focus_difficulty: 'target',
                picky_eater: 'utensils',
                meltdowns: 'waves',
                noise_sensitive: 'ear',
                sleep_issues: 'moon',
                avoids_touch: 'hand',
                transition_difficulty: 'hourglass',
                clothes_issues: 'shirt',
              }[activity.situationIds[0]] || 'star'}
              size={40}
            />
          </span>
        </div>
      )}

      <section className={styles.hero}>
        <h1 className={styles.title}>{activity.title}</h1>
        <p className={styles.description}>{activity.description}</p>

        <div className={styles.meta}>
          <span className={styles.metaChip}>
            <Icon name="timer" size={14} /> {activity.duration} min
          </span>
          <span className={styles.metaChip}>
             {activity.props.length === 0 ? 'No props' : `${activity.props.length} props`}
          </span>
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Steps</h2>
        <ol className={styles.stepsList}>
          {activity.steps.map((step, index) => {
            const hasPanelMatch = hasImages && index < images.length
            const isActive = hasImages && index === activePanel
            return (
              <li
                key={`step-${index}`}
                className={`${styles.stepItem} ${isActive ? styles.stepItemActive : ''} ${hasPanelMatch ? styles.stepItemTappable : ''}`}
                onClick={hasPanelMatch ? () => setActivePanel(index) : undefined}
              >
                {step.instruction}
              </li>
            )
          })}
        </ol>
      </section>

      <section className={styles.section}>
        <button
          className={styles.accordionHeader}
          onClick={() => setWhyExpanded(!whyExpanded)}
        >
          <h2 className={styles.sectionTitle}>Why it works</h2>
          <span className={`${styles.accordionArrow} ${whyExpanded ? styles.accordionArrowOpen : ''}`}>
            ›
          </span>
        </button>
        {whyExpanded && (
          <p className={styles.sectionText}>{activity.whyItWorks}</p>
        )}
      </section>

      <section className={styles.section}>
        <button
          className={styles.accordionHeader}
          onClick={() => setWhenExpanded(!whenExpanded)}
        >
          <h2 className={styles.sectionTitle}>When to use</h2>
          <span className={`${styles.accordionArrow} ${whenExpanded ? styles.accordionArrowOpen : ''}`}>
            ›
          </span>
        </button>
        {whenExpanded && (
          <p className={styles.sectionText}>{activity.whenToUse}</p>
        )}
      </section>

      {/* Save Toast with Reminder Prompt */}
      {showSaveToast && (
        <div className={styles.saveToast}>
          <span className={styles.saveToastText}>✓ Activity saved!</span>
          <button
            className={styles.saveToastAction}
            onClick={() => {
              setShowSaveToast(false)
              setShowReminderModal(true)
            }}
          >
            Add reminder?
          </button>
          <button
            className={styles.saveToastClose}
            onClick={() => setShowSaveToast(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      {/* Enhanced Reminder Saved Toast */}
      {reminderSaved && lastSavedReminder && (
        <div className={styles.reminderToast}>
          <div className={styles.reminderToastContent}>
            <span className={styles.reminderToastIcon}><Icon name="bell" size={22} /></span>
            <div className={styles.reminderToastInfo}>
              <span className={styles.reminderToastTitle}>Reminder set!</span>
              <span className={styles.reminderToastTime}>
                {new Date(lastSavedReminder.scheduled_at).toLocaleDateString('en-IN', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                at{' '}
                {new Date(lastSavedReminder.scheduled_at).toLocaleTimeString('en-IN', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                })}
                {lastSavedReminder.repeat_config?.type !== 'once' && (
                  <span className={styles.repeatBadge}>
                    {lastSavedReminder.repeat_config?.type === 'daily' && ' • Daily'}
                    {lastSavedReminder.repeat_config?.type === 'weekly' && ' • Weekly'}
                    {lastSavedReminder.repeat_config?.type === 'custom' && ' • Custom'}
                  </span>
                )}
              </span>
            </div>
          </div>
          <button
            className={styles.reminderToastAction}
            onClick={() => {
              setReminderSaved(false)
              setEditingReminder(lastSavedReminder)
              setShowCustomReminder(true)
            }}
          >
            Edit
          </button>
          <button
            className={styles.reminderToastClose}
            onClick={() => {
              setReminderSaved(false)
              setLastSavedReminder(null)
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      )}

      <footer className={styles.footer}>
        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={handleStart}
        >
          Start Activity
        </Button>
        <button
          className={styles.shareButton}
          onClick={() => {
            const text = `Check out this activity for our child:\n\n*${activity.title}*\n${activity.description}\n\n Steps:\n${activity.steps.map((s, i) => `${i + 1}. ${s.instruction}`).join('\n')}\n\n Why it works: ${activity.whyItWorks}\n\n— Shared from MelloMap`
            const url = `https://wa.me/?text=${encodeURIComponent(text)}`
            window.open(url, '_blank')
          }}
        >
           Share with family
        </button>
      </footer>

      {/* Reminder Modal - Main modal with list */}
      {showReminderModal && (
        <div className={styles.modalOverlay} onClick={() => setShowReminderModal(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReminderModal
              reminders={reminders}
              onAddNew={() => {
                setShowReminderModal(false)
                setShowReminderPicker(true)
              }}
              onEdit={(reminder) => {
                setEditingReminder(reminder)
                setShowReminderModal(false)
                setShowCustomReminder(true)
              }}
              onDelete={handleReminderDelete}
              onClose={() => setShowReminderModal(false)}
            />
          </div>
        </div>
      )}

      {/* Reminder Picker Modal - Quick picks for new reminder */}
      {showReminderPicker && (
        <div className={styles.modalOverlay} onClick={() => setShowReminderPicker(false)}>
          <div onClick={(e) => e.stopPropagation()}>
            <ReminderPicker
              onSave={handleReminderSave}
              onClose={() => setShowReminderPicker(false)}
              onCustomClick={() => {
                setShowReminderPicker(false)
                setEditingReminder(null)
                setShowCustomReminder(true)
              }}
            />
          </div>
        </div>
      )}

      {/* Custom Reminder Modal - Date/time picker for new or editing */}
      {showCustomReminder && (
        <div className={styles.modalOverlay} onClick={() => {
          setShowCustomReminder(false)
          setEditingReminder(null)
        }}>
          <div onClick={(e) => e.stopPropagation()}>
            <CustomReminderModal
              reminder={editingReminder}
              onSave={editingReminder
                ? (date, config) => handleReminderUpdate(editingReminder.id, date, config)
                : handleReminderSave
              }
              onClose={() => {
                setShowCustomReminder(false)
                setEditingReminder(null)
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
